import * as React from "react";
import { analysisRowKey } from "../../../agreement";
import type { RowWithIndex } from "../types";
import { getCategoryForRow } from "../utils";
import type { CategoryId } from "../tokens";
import type { AnalysisParagraphResult, AiChangeReviewStatus } from "../../../agreement";

export type AiAnalysisViewModel = {
  rows: AnalysisParagraphResult[];
  needsAttention: RowWithIndex[];
  needsReview: RowWithIndex[];
  compliant: RowWithIndex[];
  needsAttentionCount: number;
  needsReviewCount: number;
  compliantCount: number;
  reviewCount: number;
  actionIssueCount: number;
  acceptedCount: number;
  rejectedCount: number;
  actionedCount: number;
  reviewsNeededLabel: string | null;
  pendingNonCompliantKeys: Array<{ row: AnalysisParagraphResult; key: string }>;
  allActionsTaken: boolean;
  getRowsForCategory: (category: CategoryId) => RowWithIndex[];
};

function toRowWithIndex(rows: AnalysisParagraphResult[]): RowWithIndex[] {
  return rows.map((row, arrayIndex) => ({
    row,
    arrayIndex,
    key: analysisRowKey(row, arrayIndex),
  }));
}

export function useAiAnalysisViewModel(
  aiParagraphs: AnalysisParagraphResult[] | null,
  aiRowStatus: Record<string, AiChangeReviewStatus>
): AiAnalysisViewModel {
  return React.useMemo(() => {
    const rows = aiParagraphs ?? [];
    const rowsWithIndex = toRowWithIndex(rows);

    const needsAttention = rowsWithIndex.filter(
      ({ row }) => !row.compliant && row.severity === "high"
    );
    // Align with getCategoryForRow: non-high non-compliant → Review (includes low).
    const needsReview = rowsWithIndex.filter(
      ({ row }) => !row.compliant && row.severity !== "high"
    );
    const compliant = rowsWithIndex.filter(({ row }) => row.compliant);

    const needsAttentionCount = needsAttention.length;
    const needsReviewCount = needsReview.length;
    const compliantCount = compliant.length;
    const reviewCount = needsReviewCount + needsAttentionCount;
    const actionIssueCount = reviewCount;

    const pendingNonCompliantKeys = rowsWithIndex
      .filter(
        ({ row, key }) =>
          !row.compliant && (aiRowStatus[key] ?? row.reviewStatus ?? "pending") === "pending"
      )
      .map(({ row, key }) => ({ row, key }));

    const acceptedCount = Object.values(aiRowStatus).filter((s) => s === "accepted").length;
    const rejectedCount = Object.values(aiRowStatus).filter((s) => s === "rejected").length;
    const actionedCount = acceptedCount + rejectedCount;
    const allActionsTaken = reviewCount > 0 && pendingNonCompliantKeys.length === 0;

    const pendingCount = pendingNonCompliantKeys.length;
    const reviewsNeededLabel =
      pendingCount === 0
        ? null
        : pendingCount === 1
          ? "1 review needed"
          : `${pendingCount} reviews needed`;

    const getRowsForCategory = (category: CategoryId): RowWithIndex[] => {
      if (category === "needsAttention") return needsAttention;
      if (category === "needsReview") return needsReview;
      return compliant;
    };

    return {
      rows,
      needsAttention,
      needsReview,
      compliant,
      needsAttentionCount,
      needsReviewCount,
      compliantCount,
      reviewCount,
      actionIssueCount,
      acceptedCount,
      rejectedCount,
      actionedCount,
      reviewsNeededLabel,
      pendingNonCompliantKeys,
      allActionsTaken,
      getRowsForCategory,
    };
  }, [aiParagraphs, aiRowStatus]);
}

export function getRowCategory(row: AnalysisParagraphResult): CategoryId {
  return getCategoryForRow(row);
}
